/**
 * Teamkalender — was angezeigt wird, rein und getestet (__tests__/calendarModel.test.ts).
 *
 * Grundregel (Vorgabe Kevin): Team-Termine stehen bei jedem. Deshalb gilt „Meine Termine" immer —
 * Ein- und Ausblenden (Personen, Ebenen, einzelne Serien) betrifft nur fremde Termine. Wer zu einem
 * Termin eingeladen ist oder ihn organisiert, sieht ihn, egal was ausgeblendet ist.
 *
 * Zeiten kommen vom Server als Berliner Wanduhr ('JJJJ-MM-TT HH:mm:ss') — genau das Format, das
 * @mantine/schedule liest. Hier wird nichts über die Browser-Uhr umgerechnet.
 */
import type { ScheduleEventData, ScheduleResourceData } from '@mantine/schedule';
import type { UiPreferences } from '../../lib/api/types/core';
import { UNKNOWN_PERSON_COLOR, personColor } from '../../lib/personColors';
import type {
  CalendarCategory,
  CalendarEvent,
  CalendarEventInput,
  CalendarFeed,
  CalendarOccurrence,
  CalendarOutlookBusy,
  CalendarPerson,
  CalendarWhen,
} from '../../lib/api/domains/calendar';

export type CalendarView = 'day' | 'week' | 'month' | 'team' | 'list';
export type LayerKey = CalendarCategory | 'APPOINTMENTS' | 'ONCALL' | 'ABSENCES' | 'PROJECT_DATES' | 'TODOS' | 'OUTLOOK';

export const CATEGORY_META: Record<CalendarCategory, { label: string; color: string }> = {
  JOUR_FIXE: { label: 'Jour fixe', color: 'grape' },
  PROJECT: { label: 'Projekttermin', color: 'yellow' },
  CUSTOMER: { label: 'Kundentermin', color: 'teal' },
  INTERNAL: { label: 'Intern', color: 'blue' },
  TRAINING: { label: 'Schulung', color: 'orange' },
  OTHER: { label: 'Sonstiges', color: 'gray' },
};

export const CATEGORY_ORDER: CalendarCategory[] = ['JOUR_FIXE', 'PROJECT', 'CUSTOMER', 'INTERNAL', 'TRAINING', 'OTHER'];

type SourceLayer = Exclude<LayerKey, CalendarCategory>;

export const SOURCE_LAYER_META: Record<SourceLayer, { label: string; color: string; hint: string }> = {
  APPOINTMENTS: { label: 'Kundentermine (Terminplanung)', color: 'cyan', hint: 'Vorschläge und Termine aus der Terminplanung' },
  ONCALL: { label: 'Rufbereitschaft', color: 'red', hint: 'Wer außerhalb der Servicezeit erreichbar ist' },
  ABSENCES: { label: 'Abwesenheiten', color: 'gray', hint: 'Urlaub, Schulung, krank — die Art sehen nur Leitung und man selbst' },
  PROJECT_DATES: { label: 'Projekt-Stichtage', color: 'lime', hint: 'Start, Stichtag, Ende und Meilensteine' },
  TODOS: { label: 'Meine Aufgaben', color: 'pink', hint: 'Deine Aufgaben mit Fälligkeit' },
  OUTLOOK: {
    label: 'Outlook-Termine',
    color: 'indigo',
    hint: 'Termine aus Outlook, alle 10 Minuten gelesen. Den Betreff siehst nur du, die anderen sehen „Belegt".',
  },
};

export const SOURCE_LAYER_ORDER: SourceLayer[] = ['APPOINTMENTS', 'ONCALL', 'ABSENCES', 'PROJECT_DATES', 'TODOS', 'OUTLOOK'];

/**
 * Wonach die Einträge eingefärbt werden. Standard ist die Person (Wunsch Kevin, 29.09.2026): Mit
 * mehreren Kollegen in der Woche war sonst alles Outlook-Blau, und niemand sah, wessen Block das
 * ist. Die Personenfarbe ist dieselbe wie überall im ServiceHub (lib/personColors).
 */
export type ColorBy = 'person' | 'category';

export const COLOR_BY_LABEL: Record<ColorBy, string> = { person: 'Person', category: 'Terminart' };

export const VIEW_LABEL: Record<CalendarView, string> = {
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
  team: 'Team',
  list: 'Liste',
};

/** Zeile für Termine an Gruppen (ganzes Team, alle Techniker, Leitung) in der Team-Ansicht */
export const TEAM_ROW_ID = '__team__';

// ── Persönliche Einstellungen (uiPreferences.calendar) ────────────────────────────────────

export interface CalendarPrefs {
  /** Zuletzt gewählte Ansicht; null = automatisch (Handy: Liste, sonst Woche) */
  view: CalendarView | null;
  hiddenPeople: string[];
  hiddenLayers: LayerKey[];
  /** Serien/Termine „für mich ausblenden" — nur fremde, nie die eigenen */
  hiddenEvents: string[];
  favorites: string[];
  colorBy: ColorBy;
}

export const DEFAULT_CALENDAR_PREFS: CalendarPrefs = {
  view: null,
  hiddenPeople: [],
  hiddenLayers: [],
  hiddenEvents: [],
  favorites: [],
  colorBy: 'person',
};

const VIEWS: CalendarView[] = ['day', 'week', 'month', 'team', 'list'];
const LAYERS: LayerKey[] = [...CATEGORY_ORDER, ...SOURCE_LAYER_ORDER];

function stringList(value: unknown, max = 500): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string').slice(0, max) : [];
}

/** Gespeicherte Einstellungen lesen — Unbekanntes fällt weg, nie ein Absturz. */
export function readCalendarPrefs(ui: UiPreferences | null | undefined): CalendarPrefs {
  const raw = ui && typeof ui.calendar === 'object' && ui.calendar !== null ? (ui.calendar as Record<string, unknown>) : {};
  return {
    view: VIEWS.includes(raw.view as CalendarView) ? (raw.view as CalendarView) : null,
    hiddenPeople: stringList(raw.hiddenPeople),
    hiddenLayers: stringList(raw.hiddenLayers).filter((l): l is LayerKey => LAYERS.includes(l as LayerKey)),
    hiddenEvents: stringList(raw.hiddenEvents, 200),
    favorites: stringList(raw.favorites),
    colorBy: raw.colorBy === 'category' ? 'category' : 'person',
  };
}

/** Einstellungen zurückschreiben, ohne andere Einträge (Menü-Favoriten) anzufassen. */
export function writeCalendarPrefs(ui: UiPreferences | null | undefined, prefs: CalendarPrefs): UiPreferences {
  return { ...(ui ?? {}), calendar: prefs };
}

// ── Sichtbarkeit ───────────────────────────────────────────────────────────────────────────

export function isMine(event: Pick<CalendarEvent, 'attendees' | 'organizer'>, meId: string): boolean {
  return event.organizer?.id === meId || event.attendees.some((a) => a.userId === meId);
}

/** Eigene Termine immer; fremde nur, wenn Ebene, Serie und mindestens ein Teilnehmer sichtbar sind. */
export function eventVisible(event: CalendarEvent, meId: string, prefs: CalendarPrefs): boolean {
  if (isMine(event, meId)) return true;
  if (prefs.hiddenEvents.includes(event.id)) return false;
  if (prefs.hiddenLayers.includes(event.category)) return false;
  if (event.attendees.length === 0) return true;
  return event.attendees.some((a) => !prefs.hiddenPeople.includes(a.userId));
}

function personVisible(userId: string | null | undefined, meId: string, prefs: CalendarPrefs): boolean {
  if (!userId) return true;
  return userId === meId || !prefs.hiddenPeople.includes(userId);
}

// ── Zeiten ─────────────────────────────────────────────────────────────────────────────────

export function addDaysYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** 0 = Montag … 6 = Sonntag */
export function weekdayIndex(ymd: string): number {
  return (new Date(`${ymd}T12:00:00Z`).getUTCDay() + 6) % 7;
}

export function mondayOf(ymd: string): string {
  return addDaysYmd(ymd, -weekdayIndex(ymd));
}

export function berlinTodayYmd(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
}

/** 'JJJJ-MM-TT HH:mm:ss' → { date, time: 'HH:mm' } */
export function splitLocal(local: string): CalendarWhen {
  const [date, time = '00:00:00'] = local.trim().split(/[ T]/);
  return { date, time: time.slice(0, 5) };
}

/** Uhrzeit + Minuten, höchstens bis 23:59 desselben Tages */
export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export interface DateRange {
  from: string;
  to: string;
}

/** Welcher Zeitraum geladen wird. Monat: das ganze Raster (6 Wochen). */
export function viewRange(view: CalendarView, date: string): DateRange {
  if (view === 'day') return { from: date, to: date };
  if (view === 'week' || view === 'team') {
    const from = mondayOf(date);
    return { from, to: addDaysYmd(from, 6) };
  }
  if (view === 'month') {
    const from = mondayOf(`${date.slice(0, 7)}-01`);
    return { from, to: addDaysYmd(from, 41) };
  }
  return { from: date, to: addDaysYmd(date, 13) };
}

export function shiftDate(view: CalendarView, date: string, direction: -1 | 1): string {
  if (view === 'day') return addDaysYmd(date, direction);
  if (view === 'week' || view === 'team') return addDaysYmd(date, 7 * direction);
  if (view === 'list') return addDaysYmd(date, 14 * direction);
  const [y, m] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1 + direction, 1, 12));
  return next.toISOString().slice(0, 10);
}

function isoWeek(ymd: string): number {
  const d = new Date(`${ymd}T12:00:00Z`);
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4, 12));
  return 1 + Math.round(((thursday.getTime() - firstThursday.getTime()) / 86_400_000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

const fmt = (ymd: string, opts: Intl.DateTimeFormatOptions) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString('de-DE', { ...opts, timeZone: 'UTC' });

/** Überschrift des Zeitraums: „Di., 6. Oktober 2026", „KW 41 · 5.–11. Okt. 2026", „Oktober 2026" */
export function rangeLabel(view: CalendarView, date: string): string {
  if (view === 'day') return fmt(date, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  if (view === 'month') return fmt(date, { month: 'long', year: 'numeric' });
  const { from, to } = viewRange(view, date);
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  const span = sameMonth
    ? `${Number(from.slice(8))}.–${fmt(to, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : `${fmt(from, { day: 'numeric', month: 'short' })} – ${fmt(to, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return view === 'list' ? span : `KW ${isoWeek(from)} · ${span}`;
}

// ── Einträge für @mantine/schedule ────────────────────────────────────────────────────────

export type CalendarItemKind = 'event' | 'appointment' | 'oncall' | 'absence' | 'project' | 'todo' | 'outlook';

export interface CalendarItemPayload {
  kind: CalendarItemKind;
  /** Kalendertermin: Eintrag und Tag in der Serie */
  eventId?: string;
  occurrenceDate?: string;
  isSeries?: boolean;
  mine?: boolean;
  /** Outlook: Betreff sichtbar (eigener Termin, oder Admin bei nicht privaten) */
  detailed?: boolean;
  cancelled?: boolean;
  declined?: boolean;
  moved?: boolean;
  canEdit?: boolean;
  /** Zweite Zeile: Teilnehmer, Techniker, … */
  subtitle?: string | null;
  /** Ziel für Einträge der Ebenen */
  href?: string | null;
  /** Nur Anzeige (Ebenen, abgesagte, fremde) — kein Ziehen */
  readOnly: boolean;
}

export type CalendarItem = ScheduleEventData<CalendarItemPayload>;

function names(people: Map<string, string>, ids: string[]): string {
  const list = ids.map((id) => people.get(id)?.split(' ')[0] ?? '?');
  if (list.length <= 3) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
}

export function attendeeSummary(event: CalendarEvent, people: Map<string, string>): string | null {
  if (event.audience !== 'PEOPLE') {
    const label = { TEAM: 'Ganzes Team', TECHNICIANS: 'Alle Techniker', LEADERSHIP: 'Leitung' }[event.audience];
    return label;
  }
  const going = event.attendees.filter((a) => a.response !== 'DECLINED').map((a) => a.userId);
  return going.length > 0 ? names(people, going) : null;
}

function peopleMap(people: CalendarPerson[]): Map<string, string> {
  return new Map(people.map((p) => [p.id, p.displayName]));
}

/** Personenfarbe je ServiceHub-ID; wer nicht (mehr) im Team ist, bleibt grau. */
export function personColorLookup(people: CalendarPerson[]): (userId: string | null | undefined) => string {
  const colors = new Map(people.map((p) => [p.id, personColor(p.color, p.id)]));
  return (userId) => (userId ? colors.get(userId) ?? UNKNOWN_PERSON_COLOR : UNKNOWN_PERSON_COLOR);
}

interface ItemColors {
  colorBy: ColorBy;
  colorOf: (userId: string | null | undefined) => string;
}

/**
 * Farbe eines Eintrags. Nach Person: die Person, der er gehört — bei Terminen der Organisator (wer
 * eingeladen hat), sonst der erste Teilnehmer. Ohne Person (offene Rufbereitschaft, Kundentermin
 * ohne Techniker) bleibt die Farbe der Ebene, damit „noch offen" nicht wie jemand aussieht.
 */
function itemColor(c: ItemColors, fallback: string, userId: string | null | undefined): string {
  return c.colorBy === 'person' && userId ? c.colorOf(userId) : fallback;
}

function itemColors(feed: CalendarFeed, prefs: CalendarPrefs): ItemColors {
  return { colorBy: prefs.colorBy, colorOf: personColorLookup(feed.people) };
}

function allDayRange(fromYmd: string, lastYmd: string): { start: string; end: string } {
  return { start: `${fromYmd} 00:00:00`, end: `${addDaysYmd(lastYmd, 1)} 00:00:00` };
}

function occurrenceItem(
  event: CalendarEvent,
  o: CalendarOccurrence,
  meId: string,
  people: Map<string, string>,
  colors: ItemColors,
): CalendarItem {
  const mine = isMine(event, meId);
  const declined = event.me?.response === 'DECLINED';
  const cancelled = o.cancelled;
  const owner = event.organizer?.id ?? event.attendees[0]?.userId ?? null;
  return {
    id: o.key,
    title: cancelled ? `Abgesagt: ${event.title}` : event.title,
    start: o.startLocal,
    end: o.endLocal,
    color: cancelled ? 'gray' : itemColor(colors, CATEGORY_META[event.category].color, owner),
    // Eigene Termine kräftig, die der Kollegen hell — so sieht man auf einen Blick, was einen selbst betrifft.
    variant: mine && !cancelled && !declined ? 'filled' : 'light',
    payload: {
      kind: 'event',
      eventId: event.id,
      occurrenceDate: o.occurrenceDate,
      isSeries: o.isSeries,
      mine,
      cancelled,
      declined,
      moved: o.moved,
      canEdit: event.canEdit,
      subtitle: attendeeSummary(event, people),
      readOnly: !event.canEdit || cancelled,
    },
  };
}

interface LayerItemBase {
  id: string;
  title: string;
  start: string;
  end: string;
  color: string;
  payload: CalendarItemPayload;
}

export interface LayerOptions {
  /**
   * Rufbereitschaft nur als Hinweis am ersten Tag statt als Balken über die Woche. Die Ganztags-Zeile
   * der Wochen- und Tagesansicht zeigt zwei Reihen — ein Wochenbalken verdrängte dort sonst den
   * ganztägigen Team-Termin, der jemanden tatsächlich betrifft.
   */
  compactOnCall?: boolean;
}

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function weekdayTime(local: string): string {
  const { date, time } = splitLocal(local);
  return `${WEEKDAY_SHORT[weekdayIndex(date)]} ${time}`;
}

const SHOW_AS_LABEL: Record<string, string> = {
  free: 'frei',
  tentative: 'mit Vorbehalt',
  oof: 'abwesend',
  workingElsewhere: 'woanders tätig',
};

/** Zweite Zeile beim Outlook-Termin mit Details: „Outlook · privat · Ort". */
function outlookSubtitle(o: CalendarOutlookBusy): string {
  return ['Outlook', o.isPrivate ? 'privat' : null, o.showAs ? SHOW_AS_LABEL[o.showAs] ?? null : null, o.location]
    .filter(Boolean)
    .join(' · ');
}

/** Einträge der Ebenen je Person (`userId`) — für Woche/Tag/Monat und für die Team-Zeilen. */
export function layerItems(
  feed: CalendarFeed,
  meId: string,
  prefs: CalendarPrefs,
  options: LayerOptions = {},
): Array<LayerItemBase & { userId: string | null }> {
  const people = peopleMap(feed.people);
  const colors = itemColors(feed, prefs);
  const out: Array<LayerItemBase & { userId: string | null }> = [];
  const on = (layer: LayerKey) => !prefs.hiddenLayers.includes(layer);
  const firstName = (id: string | null | undefined) => (id ? people.get(id)?.split(' ')[0] ?? 'unbekannt' : null);

  if (on('APPOINTMENTS')) {
    for (const a of feed.layers.customerAppointments) {
      if (!personVisible(a.userId, meId, prefs)) continue;
      out.push({
        id: `appt:${a.id}`,
        userId: a.userId,
        title: a.title,
        start: a.startLocal,
        end: a.endLocal,
        color: itemColor(colors, SOURCE_LAYER_META.APPOINTMENTS.color, a.userId),
        payload: {
          kind: 'appointment',
          subtitle: a.userId ? people.get(a.userId) ?? a.technicianName : a.technicianName,
          href: '/appointments',
          mine: a.userId === meId,
          readOnly: true,
        },
      });
    }
  }
  if (on('ONCALL')) {
    for (const s of feed.layers.onCall) {
      if (!personVisible(s.userId, meId, prefs)) continue;
      const who = s.userId ? `Rufbereitschaft: ${firstName(s.userId)}` : 'Rufbereitschaft (noch offen)';
      const firstDay = splitLocal(s.startLocal).date;
      out.push({
        id: `oncall:${s.id}`,
        userId: s.userId,
        title: options.compactOnCall ? `${who} (ab ${splitLocal(s.startLocal).time}, bis ${weekdayTime(s.endLocal)})` : who,
        ...(options.compactOnCall ? allDayRange(firstDay, firstDay) : { start: s.startLocal, end: s.endLocal }),
        color: itemColor(colors, SOURCE_LAYER_META.ONCALL.color, s.userId),
        payload: {
          kind: 'oncall',
          subtitle: s.backupUserId ? `Backup: ${people.get(s.backupUserId) ?? '—'}` : null,
          href: '/rufbereitschaft',
          mine: s.userId === meId,
          readOnly: true,
        },
      });
    }
  }
  if (on('ABSENCES')) {
    for (const a of feed.layers.absences) {
      if (!personVisible(a.userId, meId, prefs)) continue;
      out.push({
        id: `absence:${a.id}`,
        userId: a.userId,
        title: `${people.get(a.userId) ?? 'Jemand'}: ${a.label}`,
        ...allDayRange(a.startsOn, a.endsOn),
        color: itemColor(colors, SOURCE_LAYER_META.ABSENCES.color, a.userId),
        payload: { kind: 'absence', mine: a.userId === meId, href: a.userId === meId ? '/profile' : null, readOnly: true },
      });
    }
  }
  if (on('PROJECT_DATES')) {
    for (const p of feed.layers.projects) {
      if (!personVisible(p.leadUserId, meId, prefs)) continue;
      out.push({
        id: `project:${p.key}`,
        userId: p.leadUserId,
        title: `${p.label}: ${p.title}`,
        ...allDayRange(p.date, p.date),
        color: itemColor(colors, SOURCE_LAYER_META.PROJECT_DATES.color, p.leadUserId),
        payload: {
          kind: 'project',
          subtitle: p.number + (p.done ? ' · erledigt' : ''),
          href: `/projects/${p.projectId}`,
          mine: p.leadUserId === meId,
          readOnly: true,
        },
      });
    }
  }
  if (on('TODOS')) {
    for (const t of feed.layers.todos) {
      const range = t.time
        ? { start: `${t.date} ${t.time}:00`, end: `${t.date} ${addMinutesToTime(t.time, 15)}:00` }
        : allDayRange(t.date, t.date);
      out.push({
        id: `todo:${t.id}`,
        userId: meId,
        title: `Aufgabe: ${t.title}`,
        ...range,
        color: itemColor(colors, SOURCE_LAYER_META.TODOS.color, meId),
        payload: { kind: 'todo', href: `/reminders?open=${t.id}`, mine: true, readOnly: true },
      });
    }
  }
  if (on('OUTLOOK')) {
    for (const o of feed.layers.outlook ?? []) {
      if (!personVisible(o.userId, meId, prefs)) continue;
      out.push({
        id: `outlook:${o.id}`,
        userId: o.userId,
        title: o.title,
        start: o.startLocal,
        end: o.endLocal,
        color: itemColor(colors, SOURCE_LAYER_META.OUTLOOK.color, o.userId),
        payload: {
          kind: 'outlook',
          subtitle: o.mine
            ? outlookSubtitle(o)
            : o.detailed
              ? [people.get(o.userId), outlookSubtitle(o)].filter(Boolean).join(' · ')
              : people.get(o.userId) ?? null,
          mine: o.mine,
          detailed: o.detailed ?? o.mine,
          readOnly: true,
        },
      });
    }
  }
  return out;
}

/** Tag, Woche, Monat, Liste: alle sichtbaren Termine in einer Fläche. */
export function buildCalendarItems(
  feed: CalendarFeed,
  meId: string,
  prefs: CalendarPrefs,
  options: LayerOptions = {},
): CalendarItem[] {
  const people = peopleMap(feed.people);
  const colors = itemColors(feed, prefs);
  const events = new Map(feed.events.map((e) => [e.id, e]));
  const items: CalendarItem[] = [];
  for (const o of feed.occurrences) {
    const event = events.get(o.eventId);
    if (!event || !eventVisible(event, meId, prefs)) continue;
    items.push(occurrenceItem(event, o, meId, people, colors));
  }
  for (const l of layerItems(feed, meId, prefs, options)) {
    items.push({ id: l.id, title: l.title, start: l.start, end: l.end, color: l.color, variant: 'light', payload: l.payload });
  }
  return items;
}

/**
 * Team-Ansicht: je Person eine Zeile, dazu oben eine für Termine ans ganze Team (bzw. alle Techniker,
 * Leitung) — sonst stünde derselbe Termin in jeder Zeile. Du selbst zuerst, dann die Favoriten.
 */
export function buildTeamResources(feed: CalendarFeed, meId: string, prefs: CalendarPrefs): ScheduleResourceData[] {
  const visible = feed.people.filter((p) => p.id === meId || !prefs.hiddenPeople.includes(p.id));
  const rank = (p: CalendarPerson) => (p.id === meId ? 0 : prefs.favorites.includes(p.id) ? 1 : 2);
  const sorted = [...visible].sort((a, b) => rank(a) - rank(b) || a.displayName.localeCompare(b.displayName, 'de'));
  return [
    { id: TEAM_ROW_ID, label: 'Ganzes Team' },
    ...sorted.map((p) => ({
      id: p.id,
      label: p.id === meId ? `${p.displayName} (du)` : p.displayName,
      color: personColor(p.color, p.id),
    })),
  ];
}

export function buildTeamItems(feed: CalendarFeed, meId: string, prefs: CalendarPrefs): CalendarItem[] {
  const people = peopleMap(feed.people);
  const colors = itemColors(feed, prefs);
  const rows = new Set(buildTeamResources(feed, meId, prefs).map((r) => String(r.id)));
  const events = new Map(feed.events.map((e) => [e.id, e]));
  const items: CalendarItem[] = [];
  for (const o of feed.occurrences) {
    const event = events.get(o.eventId);
    if (!event || !eventVisible(event, meId, prefs)) continue;
    const base = occurrenceItem(event, o, meId, people, colors);
    if (event.audience !== 'PEOPLE') {
      items.push({ ...base, id: `${o.key}:${TEAM_ROW_ID}`, resourceId: TEAM_ROW_ID });
      continue;
    }
    for (const a of event.attendees) {
      if (a.response === 'DECLINED' || !rows.has(a.userId)) continue;
      items.push({ ...base, id: `${o.key}:${a.userId}`, resourceId: a.userId });
    }
  }
  for (const l of layerItems(feed, meId, prefs)) {
    const row = l.userId && rows.has(l.userId) ? l.userId : TEAM_ROW_ID;
    items.push({ id: l.id, title: l.title, start: l.start, end: l.end, color: l.color, variant: 'light', payload: l.payload, resourceId: row });
  }
  return items;
}

// ── Liste (Handy) ──────────────────────────────────────────────────────────────────────────

export interface ListDay {
  day: string;
  items: CalendarItem[];
}

/** Tage mit Einträgen; mehrtägige stehen an jedem ihrer Tage (so steht „Urlaub" auch heute da). */
export function buildListDays(items: CalendarItem[], range: DateRange): ListDay[] {
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const start = String(item.start).slice(0, 10);
    const endRaw = String(item.end);
    // Ende exklusiv: Mitternacht gehört zum Vortag.
    const endDay = endRaw.endsWith('00:00:00') && endRaw.slice(0, 10) > start ? addDaysYmd(endRaw.slice(0, 10), -1) : endRaw.slice(0, 10);
    for (let d = start < range.from ? range.from : start; d <= endDay && d <= range.to; d = addDaysYmd(d, 1)) {
      const list = byDay.get(d) ?? [];
      list.push(item);
      byDay.set(d, list);
    }
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, list]) => ({
      day,
      items: list.sort((a, b) => String(a.start).localeCompare(String(b.start)) || a.title.localeCompare(b.title)),
    }));
}

/**
 * Zeitangabe eines Eintrags an einem bestimmten Tag: „10:00–11:00", mehrtägig „ab 18:00" am
 * ersten, „bis 08:00" am letzten und „ganztägig" an den Tagen dazwischen.
 */
export function dayTimeLabel(item: Pick<CalendarItem, 'start' | 'end'>, day: string): string {
  if (isAllDayItem(item)) return 'ganztägig';
  const s = splitLocal(String(item.start));
  const e = splitLocal(String(item.end));
  const startsToday = s.date === day;
  const endsToday = e.date === day && e.time !== '00:00';
  if (startsToday && endsToday) return `${s.time}–${e.time}`;
  if (startsToday) return `ab ${s.time}`;
  if (endsToday) return `bis ${e.time}`;
  return 'ganztägig';
}

/** Team-Raster: je Zeile (Person bzw. „ganzes Team") und Tag die Einträge, sortiert. */
export function buildTeamGrid(items: CalendarItem[], range: DateRange): Map<string, Map<string, CalendarItem[]>> {
  const byRow = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const row = String(item.resourceId ?? TEAM_ROW_ID);
    const list = byRow.get(row) ?? [];
    list.push(item);
    byRow.set(row, list);
  }
  const grid = new Map<string, Map<string, CalendarItem[]>>();
  for (const [row, list] of byRow) {
    grid.set(row, new Map(buildListDays(list, range).map((d) => [d.day, d.items])));
  }
  return grid;
}

export function isAllDayItem(item: Pick<CalendarItem, 'start' | 'end'>): boolean {
  return String(item.start).endsWith('00:00:00') && String(item.end).endsWith('00:00:00') && String(item.end) > String(item.start);
}

// ── Bearbeiten ─────────────────────────────────────────────────────────────────────────────

/** Vollständige Eingabe aus einem gespeicherten Termin — für Änderungen, die nur die Zeit betreffen. */
export function eventToInput(event: CalendarEvent): CalendarEventInput {
  return {
    title: event.title,
    description: event.description,
    location: event.location,
    onlineUrl: event.onlineUrl,
    category: event.category,
    isPrivate: event.isPrivate,
    allDay: event.allDay,
    start: event.start,
    end: event.end,
    recurrence: event.recurrence,
    recurrenceUntil: event.recurrenceUntil,
    audience: event.audience,
    audienceRequired: event.audienceRequired,
    attendees: event.attendees.filter((a) => a.source === 'INVITED').map((a) => ({ userId: a.userId, required: a.required })),
    customerId: event.customer?.id ?? null,
    projectId: event.project?.id ?? null,
    ticket: event.ticket,
    reminderMinutes: event.reminderMinutes,
  };
}

/** Neue Zeiten aus Ziehen/Loslassen: ganztägig bleibt ganztägig (Ende = letzter Tag). */
export function droppedTimes(allDay: boolean, newStart: string, newEnd: string): { start: CalendarWhen; end: CalendarWhen } {
  const start = splitLocal(newStart);
  const end = splitLocal(newEnd);
  if (!allDay) return { start, end };
  const lastDay = end.time === '00:00' && end.date > start.date ? addDaysYmd(end.date, -1) : end.date;
  return { start: { date: start.date, time: null }, end: { date: lastDay, time: null } };
}
